import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="modal"
export default class extends Controller {
  static targets = ["dialog", "template", "openFullPageLink"]
  
  static values = {
    open: { type: Boolean, default: false },
    lazyLoad: { type: Boolean, default: false },
    turboFrameSrc: { type: String, default: "" },
    preventDismiss: { type: Boolean, default: false }
  }

  connect() {
    // Handle Turbo caching - close modal before page is cached
    document.addEventListener("turbo:before-cache", this.closeBeforeCache)
    
    // Listen for custom close event from turbo streams
    window.addEventListener("modal:close-requested", this.handleCloseRequest)
    
    // Listen for turbo-frame load to focus first input
    this.element.addEventListener("turbo:frame-load", this.handleFrameLoad)
    
    // Track if we pushed a history state
    this.pushedState = false
    
    // Open modal on initial load if open value is true
    if (this.openValue) {
      this.open()
    }
  }

  disconnect() {
    document.removeEventListener("turbo:before-cache", this.closeBeforeCache)
    window.removeEventListener("modal:close-requested", this.handleCloseRequest)
    this.element.removeEventListener("turbo:frame-load", this.handleFrameLoad)
    
    // Clean up body scroll lock
    document.body.style.overflow = ""
  }

  // Open modal as a modal dialog
  open(event) {
    if (event) event.preventDefault()
    
    // Handle lazy loading
    if (this.lazyLoadValue && this.hasTemplateTarget) {
      this.loadTemplateContent()
    }
    
    // Handle Turbo Frame lazy loading, modal size, modal title, and preventDismiss - check both modal container and triggering button
    let turboFrameSrc = this.turboFrameSrcValue
    let modalSize = null
    let modalTitle = null
    let modalTitleVerbatim = false
    let preventDismiss = null

    if (event && event.target) {
      // Find the element with the data attributes, traversing up the DOM tree if needed
      let element = event.target
      let buttonTurboFrameSrc = null
      let buttonModalSize = null
      let buttonModalTitle = null
      let buttonModalTitleVerbatim = null
      let buttonPreventDismiss = null

      while (element && element !== document.body) {
        if (!buttonTurboFrameSrc) {
          buttonTurboFrameSrc = element.getAttribute('data-modal-turbo-frame-src-value')
        }
        if (!buttonModalSize) {
          buttonModalSize = element.getAttribute('data-modal-size-value')
        }
        if (!buttonModalTitle) {
          buttonModalTitle = element.getAttribute('data-modal-title-value')
        }
        if (!buttonModalTitleVerbatim) {
          buttonModalTitleVerbatim = element.getAttribute('data-modal-title-verbatim-value')
        }
        if (!buttonPreventDismiss) {
          buttonPreventDismiss = element.getAttribute('data-modal-prevent-dismiss-value')
        }
        // Continue searching until we've checked all possible elements
        element = element.parentElement
      }

      if (buttonTurboFrameSrc) {
        turboFrameSrc = buttonTurboFrameSrc
      }
      if (buttonModalSize) {
        modalSize = buttonModalSize
      }
      if (buttonModalTitle) {
        modalTitle = buttonModalTitle
      }
      if (buttonModalTitleVerbatim) {
        modalTitleVerbatim = buttonModalTitleVerbatim === 'true'
      }
      if (buttonPreventDismiss) {
        preventDismiss = buttonPreventDismiss === 'true'
      }
    }
    
    if (turboFrameSrc) {
      this.loadTurboFrame(turboFrameSrc)
      // Set the full page link URL
      this.setFullPageLink(turboFrameSrc)
    }
    
    // Apply modal size if specified
    if (modalSize) {
      this.applyModalSize(modalSize)
    }
    
    // Apply modal title if specified
    if (modalTitle) {
      this.applyModalTitle(modalTitle, modalTitleVerbatim)
    }
    
    // Apply preventDismiss if specified
    if (preventDismiss !== null) {
      this.applyPreventDismiss(preventDismiss)
    }
    
    // Show the modal
    this.dialogTarget.showModal()
    
    // Lock body scroll
    document.body.style.overflow = "hidden"
    
    // Remove closing attribute if present
    this.dialogTarget.removeAttribute("data-closing")
    
    // Update URL in browser history if we have a turbo frame source
    if (turboFrameSrc && turboFrameSrc !== "" && turboFrameSrc !== "#") {
      // Store the current URL and its history state before pushing new state
      this.originalUrl = window.location.href
      this.originalState = history.state
      // Push the new URL to history
      history.pushState({ modal: true }, "", turboFrameSrc)
      this.pushedState = true
    }
    
    // Dispatch custom event
    this.dispatch("opened")
  }

  // Close modal with animation
  close(event) {
    if (event) event.preventDefault()
    
    // Don't close if preventDismiss is true
    if (this.preventDismissValue) {
      return
    }
    
    // Add closing state for animation
    this.dialogTarget.setAttribute("data-closing", "")
    
    // Wait for animation to complete before actually closing
    setTimeout(() => {
      this.dialogTarget.close()
      this.dialogTarget.removeAttribute("data-closing")
      
      // Unlock body scroll
      document.body.style.overflow = ""
      
      // Clear the turbo-frame content to prevent stale data
      const turboFrame = this.dialogTarget.querySelector("turbo-frame[data-modal-frame]")
      if (turboFrame) {
        turboFrame.removeAttribute("src")
        turboFrame.innerHTML = '<div class="turbo-loading"></div>'
      }
      
      // Hide the full page link
      if (this.hasOpenFullPageLinkTarget) {
        this.openFullPageLinkTarget.href = "#"
        this.openFullPageLinkTarget.style.display = "none"
      }
      
      // Restore original URL if we pushed a state
      if (this.pushedState && this.originalUrl) {
        // Use replaceState to avoid adding another history entry. Write back
        // the state captured on open, not null: Turbo keeps its
        // restorationIdentifier in history.state, and its popstate handler
        // ignores an entry without one — so Back to this page would change
        // the URL and leave the next page on screen (TASK-201).
        history.replaceState(this.originalState, "", this.originalUrl)
        this.pushedState = false
        this.originalUrl = null
        this.originalState = null
      }
      
      // Dispatch custom event
      this.dispatch("closed")
    }, 200) // Match animation duration in CSS
  }

  // Close this modal, then hand off to the slideover controller once it's
  // fully closed — a "Create my own" escape hatch inside a pick modal
  // (TASK-226.8). The slideover element is a SIBLING outside this modal's
  // own subtree (both live at the foot of layouts/application.html.erb), so
  // a plain click->slideover#open action on a link in here can never resolve
  // it: Stimulus only matches an ancestor. Read the target off this same
  // element (same data attributes `slideover#open` itself reads off its
  // trigger) and hand them to the slideover via a custom event instead —
  // mirrors the existing close-requested convention, just for opening.
  closeAndOpenSlideover(event) {
    const el = event?.target?.closest("[data-slideover-turbo-frame-src-value]")
    const detail = {
      turboFrameSrc: el?.getAttribute("data-slideover-turbo-frame-src-value"),
      title: el?.getAttribute("data-slideover-title-value"),
      direction: el?.getAttribute("data-slideover-direction-value"),
      size: el?.getAttribute("data-slideover-size-value")
    }

    this.element.addEventListener("modal:closed", () => {
      window.dispatchEvent(new CustomEvent("slideover:open-requested", { detail }))
    }, { once: true })

    this.close(event)
  }

  // Handle backdrop click (clicking outside modal content)
  backdropClose(event) {
    // Only close if clicking directly on the dialog (backdrop), not its children
    if (event.target === this.dialogTarget) {
      if (!this.preventDismissValue) {
        this.close(event)
      } else {
        // Show visual feedback animation when preventDismiss is true
        this.showPreventDismissAnimation()
      }
    }
  }

  // Handle escape key
  cancel(event) {
    // Always prevent the default browser behavior first
    event.preventDefault()
    event.stopPropagation()
    
    // Check if preventDismiss is enabled (check both the value and the data attribute)
    const preventDismiss = this.preventDismissValue || this.element.dataset.modalPreventDismissValue === 'true'
    
    if (preventDismiss) {
      // Show visual feedback animation when preventDismiss is true
      this.showPreventDismissAnimation()
      // Important: return early to prevent any further execution
      return false
    }
    
    // Only close if dismissal is allowed
    this.close()
  }

  // Alias for close
  hide(event) {
    this.close(event)
  }

  // Show modal non-modally (for special use cases)
  show(event) {
    if (event) event.preventDefault()
    
    this.dialogTarget.show()
    document.body.style.overflow = "hidden"
  }

  // Load template content (Template-based Lazy Loading)
  loadTemplateContent() {
    if (!this.hasTemplateTarget) return
    
    const template = this.templateTarget
    const content = template.content.cloneNode(true)
    
    // Find the modal body or insert point
    const modalBody = this.dialogTarget.querySelector(".modal-body")
    if (modalBody) {
      // Clear existing loading state if any
      modalBody.innerHTML = ""
      modalBody.appendChild(content)
    }
    
    // Mark as loaded so we don't load again
    this.lazyLoadValue = false
  }

  // Load Turbo Frame content (Turbo Frame Lazy Loading)
  loadTurboFrame(src) {
    const turboFrame = this.dialogTarget.querySelector("turbo-frame[data-modal-frame]")
    if (turboFrame) {
      // Remove src first if it exists, then set the new one
      // This forces Turbo to reload even if it's the same URL
      if (turboFrame.hasAttribute("src")) {
        turboFrame.removeAttribute("src")
        // Force reflow to ensure Turbo recognizes the change
        turboFrame.offsetHeight
      }
      turboFrame.setAttribute("src", src)
    }
  }

  // Apply modal size class to modal content
  applyModalSize(size) {
    const modalContent = this.dialogTarget.querySelector(".modal-content")
    if (modalContent) {
      // Remove existing size classes
      modalContent.classList.remove("modal-sm", "modal-md", "modal-lg", "modal-xl", "modal-2xl", "modal-3xl", "modal-4xl", "modal-5xl", "modal-6xl", "modal-full", "modal-preview")
      // Add the new size class
      modalContent.classList.add(size)
    }
  }

  // Apply modal title to modal header
  //
  // `verbatim` opts the heading out of the Title Case rule in modal.scss, for
  // titles built from user-entered text (TASK-178). One dialog element is
  // reused for every modal, so the class is always toggled — never only added —
  // or a verbatim title would leak its exemption onto the next modal opened.
  applyModalTitle(title, verbatim = false) {
    const modalTitleElement = this.dialogTarget.querySelector(".modal-header h3, .modal-header h2, .modal-header h1")
    if (modalTitleElement) {
      modalTitleElement.textContent = title
      modalTitleElement.classList.toggle("modal-title-verbatim", Boolean(verbatim))
    }
  }

  // Apply preventDismiss setting to modal
  applyPreventDismiss(preventDismiss) {
    // Set the Stimulus value (this updates the data attribute on the controller element)
    this.preventDismissValue = preventDismiss
    
    // Also explicitly set the data attribute to ensure it persists
    this.element.dataset.modalPreventDismissValue = preventDismiss.toString()
  }

  // Show visual feedback animation when trying to dismiss a modal with preventDismiss enabled
  showPreventDismissAnimation() {
    const modalContent = this.dialogTarget.querySelector(".modal-content")
    if (modalContent) {
      // Add the animation class
      modalContent.classList.add("modal-prevent-dismiss-shake")
      
      // Remove the animation class after animation completes
      setTimeout(() => {
        modalContent.classList.remove("modal-prevent-dismiss-shake")
      }, 300) // Match animation duration
    }
  }

  // Set the full page link URL
  setFullPageLink(url) {
    if (this.hasOpenFullPageLinkTarget && url && url !== "" && url !== "#") {
      this.openFullPageLinkTarget.href = url
      this.openFullPageLinkTarget.style.display = ""
    }
  }

  // Close before Turbo caches the page
  closeBeforeCache = () => {
    if (this.dialogTarget.open) {
      this.dialogTarget.close()
      document.body.style.overflow = ""
    }
  }

  // Handle close request from turbo stream
  handleCloseRequest = () => {
    if (this.dialogTarget.open) {
      this.close()
    }
  }

  // Handle turbo frame load to focus first input
  handleFrameLoad = () => {
    if (this.dialogTarget.open) {
      setTimeout(() => {
        const firstInput = this.dialogTarget.querySelector('input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled])')
        if (firstInput) {
          firstInput.focus()
        }
      }, 50)
    }
  }
}

